import pluralize from "pluralize";

import { formatShortDate } from "@intelligems/ig-utils/date-time";
import { CurrencyFormat } from "@src/features/admin/components/utils";

import type {
  BillingEntityStatusViewModel,
  BillingPastSubscriptionViewModel,
} from "../types";

import { isTerminalSubscriptionStatus } from "./lapsedFreeTrial";

const EMPTY_VALUE = "—";
const UNNAMED_SUBSCRIPTION = "Intelligems";

/**
 * `createdAtTs` is epoch milliseconds, set by the database timestamp hook, while
 * every Stripe-sourced timestamp on the same summary is epoch seconds.
 */
const millisecondsToSeconds = (
  ms: number | null | undefined
): number | null | undefined => (ms == null ? ms : Math.floor(ms / 1_000));

const STATUS_VIEW_MODELS: Record<string, BillingEntityStatusViewModel> = {
  canceled: { label: "Cancelled", tone: "error" },
  cancelled: { label: "Cancelled", tone: "error" },
  incomplete_expired: { label: "Expired", tone: "default" },
  unpaid: { label: "Unpaid", tone: "error" },
};

const toStatusViewModel = (
  status: string | null | undefined
): BillingEntityStatusViewModel =>
  STATUS_VIEW_MODELS[status ?? ""] ?? { label: "Ended", tone: "default" };

const formatAmount = (
  price: number | null | undefined,
  interval: string | null | undefined,
  intervalCount: number | null | undefined
): string => {
  // `price == null`, not `!price`: a fully discounted or metered subscription
  // really did bill $0.00, which is a fact rather than a missing value.
  if (price == null || !interval) {
    return EMPTY_VALUE;
  }

  const count = intervalCount ?? 1;
  const formattedPrice = CurrencyFormat.format(price);

  return count === 1
    ? `${formattedPrice} per ${interval}`
    : `${formattedPrice} every ${count} ${pluralize(interval, count)}`;
};

/**
 * The lifetime of a dead subscription — when it started through when it stopped —
 * rather than its final billing cycle, which says nothing useful about a plan the
 * merchant no longer has.
 *
 * Legacy records carry only a creation timestamp. Those render as `From 3/15/26`
 * rather than a bare date, which under a "Period" heading would leave the reader
 * guessing whether it was the start or the end.
 */
const formatPeriod = (
  startTs: number | null | undefined,
  endTs: number | null | undefined
): string => {
  if (!startTs) {
    return endTs ? formatShortDate(endTs) : EMPTY_VALUE;
  }

  return endTs
    ? `${formatShortDate(startTs)} - ${formatShortDate(endTs)}`
    : `From ${formatShortDate(startTs)}`;
};

export type RawPastStripeSubscription = {
  id: string;
  stripeSubscriptionId?: string | null;
  status?: string | null;
  interval?: string | null;
  intervalCount?: number | null;
  createdAtTs?: number | null;
  endedAt?: number | null;
  periodEnd?: number | null;
  stripeProductLinks: ReadonlyArray<{
    stripeProduct?: { name: string; flatPrice: number } | null;
  }>;
};

export type PastSubscriptionPaidAmount = {
  amountPaidCents: number | null;
};

export type RawPastLegacySubscription = {
  name: string;
  status?: string | null;
  interval?: string | null;
  price?: number | null;
  createdAtTs?: number | null;
};

export type PastSubscriptionsInput = {
  organizationSubscriptions?: ReadonlyArray<RawPastLegacySubscription>;
  stripeSubscriptionSummaries?: ReadonlyArray<RawPastStripeSubscription>;
  paidAmountsBySubscriptionId?: Readonly<
    Record<string, PastSubscriptionPaidAmount>
  >;
  isPaidAmountsLoading?: boolean;
};

const toStripeRow = (
  summary: RawPastStripeSubscription,
  paidAmountsBySubscriptionId: Readonly<
    Record<string, PastSubscriptionPaidAmount>
  >,
  isPaidAmountsLoading: boolean
): BillingPastSubscriptionViewModel => {
  const products = summary.stripeProductLinks.flatMap((link) =>
    link.stripeProduct ? [link.stripeProduct] : []
  );
  const name =
    Array.from(new Set(products.map((product) => product.name))).join(", ") ||
    UNNAMED_SUBSCRIPTION;
  // No priced line items means the catalog price is unknown, which is not the same as
  // zero — summing an empty list into 0 would state a price the record never had.
  const catalogPrice =
    products.length > 0
      ? products.reduce((total, product) => total + product.flatPrice, 0)
      : null;
  const endedAtTs = summary.endedAt ?? summary.periodEnd ?? 0;
  const createdAtSeconds = millisecondsToSeconds(summary.createdAtTs);

  const paidCents = summary.stripeSubscriptionId
    ? paidAmountsBySubscriptionId[summary.stripeSubscriptionId]?.amountPaidCents
    : undefined;

  const isResolved = !isPaidAmountsLoading && paidCents != null;

  return {
    amount: isResolved
      ? formatAmount(paidCents / 100, summary.interval, summary.intervalCount)
      : EMPTY_VALUE,
    amountState: isPaidAmountsLoading
      ? "loading"
      : isResolved
        ? "resolved"
        : "unavailable",
    dedupeAmount: formatAmount(
      catalogPrice,
      summary.interval,
      summary.intervalCount
    ),
    id: summary.id,
    name,
    period: formatPeriod(createdAtSeconds, endedAtTs || null),
    sortTs: endedAtTs,
    status: toStatusViewModel(summary.status),
  };
};

const toLegacyRow = (
  subscription: RawPastLegacySubscription
): BillingPastSubscriptionViewModel => {
  const amount = formatAmount(subscription.price, subscription.interval, 1);
  const createdAtSeconds = millisecondsToSeconds(subscription.createdAtTs);

  return {
    amount,
    amountState: "resolved",
    dedupeAmount: amount,
    id: `legacy-${subscription.name}-${subscription.createdAtTs ?? 0}`,
    name: subscription.name,
    period: formatPeriod(createdAtSeconds, null),
    sortTs: createdAtSeconds ?? 0,
    status: toStatusViewModel(subscription.status),
  };
};

const identityKey = ({
  dedupeAmount,
  name,
}: BillingPastSubscriptionViewModel) => `${name}|${dedupeAmount}`;

export const buildPastSubscriptions = ({
  organizationSubscriptions = [],
  stripeSubscriptionSummaries = [],
  paidAmountsBySubscriptionId = {},
  isPaidAmountsLoading = false,
}: PastSubscriptionsInput): BillingPastSubscriptionViewModel[] => {
  const stripeRows = stripeSubscriptionSummaries
    .filter((summary) => isTerminalSubscriptionStatus(summary.status))
    .map((summary) =>
      toStripeRow(summary, paidAmountsBySubscriptionId, isPaidAmountsLoading)
    );

  const stripeRowKeys = new Set(stripeRows.map(identityKey));

  const legacyRows = organizationSubscriptions
    .filter((subscription) => isTerminalSubscriptionStatus(subscription.status))
    .map(toLegacyRow)
    .filter((row) => !stripeRowKeys.has(identityKey(row)));

  return [...stripeRows, ...legacyRows].sort((a, b) => b.sortTs - a.sortTs);
};
